import React, { useCallback, useMemo } from 'react';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Grid from '@mui/material/Grid2';
import Autocomplete from '@mui/material/Autocomplete';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import Add from '@mui/icons-material/Add';
import Delete from '@mui/icons-material/Delete';
import { TemplateTypeEnum, LogsTemplateField, LogTemplateType } from '@/__generated__/graphql';
import {
  HOST_TEMPLATE_DISPLAY,
  FIELD_KEY_AUTOCOMPLETE_HINT,
  OTHER_LOG_TEMPLATE_SEARCH_ORDER,
  LOGS_TEMPLATE_FIELD_SOURCE_LABEL,
} from '@/components/TemplateBuilder/constants';

export interface Variable {
  fieldKey: string;
  name: string;
  templateType: TemplateTypeEnum;
  isBlisterField?: boolean;
  /** Persisted on save: which logs template supplies `fieldKey` (null = same template as the equation). */
  logsTemplateFieldSource?: LogTemplateType | null;
}

function resolveLogsTemplateFieldSource(
  fieldKey: string,
  hostLogTemplateType: LogTemplateType,
  indirectPobFieldKeys: ReadonlySet<string>,
  worklogFieldKeys: ReadonlySet<string>,
  costLogFieldKeys: ReadonlySet<string>,
  qaqcFieldKeys: ReadonlySet<string>
): LogTemplateType | null {
  const fieldKeySetByTemplateType: Partial<Record<LogTemplateType, ReadonlySet<string>>> = {
    [LogTemplateType.IndirectPobTemplate]: indirectPobFieldKeys,
    [LogTemplateType.WorklogTemplate]: worklogFieldKeys,
    [LogTemplateType.CostLogTemplate]: costLogFieldKeys,
    [LogTemplateType.QaqcTemplate]: qaqcFieldKeys,
  };

  const hostFieldKeySet = fieldKeySetByTemplateType[hostLogTemplateType];
  if (hostFieldKeySet?.has(fieldKey)) {
    return null;
  }

  for (const templateType of OTHER_LOG_TEMPLATE_SEARCH_ORDER[hostLogTemplateType] ?? []) {
    if (fieldKeySetByTemplateType[templateType]?.has(fieldKey)) {
      return templateType;
    }
  }

  return null;
}

interface VariableEditorProps {
  variables: Variable[];
  availableFieldKeys: string[];
  availableFields: LogsTemplateField[];
  /** Field keys on the structure’s Indirect POB logs template. */
  indirectPobFieldKeys: ReadonlySet<string>;
  /** Field keys on the structure’s work log template. */
  worklogFieldKeys: ReadonlySet<string>;
  /** Field keys on the structure’s cost log template. */
  costLogFieldKeys: ReadonlySet<string>;
  /** Field keys on the structure’s QA/QC logs template (when equations live on that template). */
  qaqcFieldKeys: ReadonlySet<string>;
  /** Template that owns these equations (native field keys use null `logsTemplateFieldSource`). */
  hostLogTemplateType: LogTemplateType;
  mode: 'ReadOnly' | 'Edit';
  onVariablesChange: (variables: Variable[]) => void;
  onCreateNewField?: (fieldKey: string, equationKey: string) => void;
  equationKey: string;
}

export const VariableEditor: React.FC<VariableEditorProps> = ({
  variables,
  availableFieldKeys,
  availableFields,
  indirectPobFieldKeys,
  worklogFieldKeys,
  costLogFieldKeys,
  qaqcFieldKeys,
  hostLogTemplateType,
  mode,
  onVariablesChange,
  onCreateNewField,
  equationKey,
}) => {
  const getFieldNameByKey = useCallback(
    (fieldKey: string) => {
      const field = availableFields.find((f) => f.key === fieldKey);
      return field?.name ?? fieldKey;
    },
    [availableFields]
  );

  const addVariable = useCallback(() => {
    const fieldKey = availableFieldKeys[0] ?? '';
    const newVariable: Variable = {
      fieldKey,
      name: 'New Variable',
      templateType: TemplateTypeEnum.Workpack,
      logsTemplateFieldSource: resolveLogsTemplateFieldSource(
        fieldKey,
        hostLogTemplateType,
        indirectPobFieldKeys,
        worklogFieldKeys,
        costLogFieldKeys,
        qaqcFieldKeys
      ),
    };
    onVariablesChange([...variables, newVariable]);
  }, [
    variables,
    availableFieldKeys,
    hostLogTemplateType,
    indirectPobFieldKeys,
    worklogFieldKeys,
    costLogFieldKeys,
    qaqcFieldKeys,
    onVariablesChange,
  ]);

  const updateVariable = useCallback(
    (index: number, updates: Partial<Variable>) => {
      const updatedVariables = variables.map((variable, i) =>
        i === index ? { ...variable, ...updates } : variable
      );
      onVariablesChange(updatedVariables);
    },
    [variables, onVariablesChange]
  );

  const applyFieldKeyAtIndex = useCallback(
    (varIndex: number, fieldKey: string) => {
      const fieldName = getFieldNameByKey(fieldKey);
      const logsTemplateFieldSource = resolveLogsTemplateFieldSource(
        fieldKey,
        hostLogTemplateType,
        indirectPobFieldKeys,
        worklogFieldKeys,
        costLogFieldKeys,
        qaqcFieldKeys
      );
      updateVariable(varIndex, {
        fieldKey,
        name: fieldName,
        logsTemplateFieldSource,
      });
    },
    [
      getFieldNameByKey,
      hostLogTemplateType,
      indirectPobFieldKeys,
      worklogFieldKeys,
      costLogFieldKeys,
      qaqcFieldKeys,
      updateVariable,
    ]
  );

  const newFieldCreatedOnHostMessage = useMemo(() => {
    const templateName = HOST_TEMPLATE_DISPLAY[hostLogTemplateType];
    return templateName
      ? `New field will be created on this ${templateName} template if missing`
      : 'New field will be created on this logs template if missing';
  }, [hostLogTemplateType]);

  const fieldKeyAutocompleteHint = useMemo(() => {
    return (
      FIELD_KEY_AUTOCOMPLETE_HINT[hostLogTemplateType] ?? 'Any field key from another logs template'
    );
  }, [hostLogTemplateType]);

  const deleteVariable = useCallback(
    (index: number) => {
      const updatedVariables = variables.filter((_, i) => i !== index);
      onVariablesChange(updatedVariables);
    },
    [variables, onVariablesChange]
  );

  return (
    <Stack spacing={2}>
      {mode === 'Edit' && (
        <Button
          startIcon={<Add />}
          variant="outlined"
          size="small"
          onClick={addVariable}
          sx={{ alignSelf: 'flex-start' }}
        >
          Add Variable
        </Button>
      )}

      {variables.length > 0 ? (
        <Stack spacing={2}>
          {variables.map((variable, varIndex) => (
            <Card
              key={`${variable.fieldKey ?? 'var'}-${variable.name ?? 'unnamed'}-${varIndex}`}
              variant="outlined"
              sx={{ bgcolor: 'grey.50' }}
            >
              <CardContent>
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Variable Name"
                      value={variable.name ?? ''}
                      onChange={(e) =>
                        updateVariable(varIndex, {
                          name: e.target.value,
                        })
                      }
                      disabled={mode === 'ReadOnly'}
                      size="small"
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 5 }}>
                    <Stack spacing={0.5}>
                      <Autocomplete
                        fullWidth
                        size="small"
                        disabled={mode === 'ReadOnly'}
                        freeSolo
                        autoHighlight
                        forcePopupIcon
                        popupIcon={<ArrowDropDown />}
                        value={variable.fieldKey ?? ''}
                        options={availableFieldKeys}
                        onChange={(_, newValue) => {
                          if (newValue) {
                            const fieldKey = String(newValue);
                            applyFieldKeyAtIndex(varIndex, fieldKey);

                            if (!availableFieldKeys.includes(fieldKey) && onCreateNewField) {
                              onCreateNewField(fieldKey, equationKey);
                            }
                          }
                        }}
                        onInputChange={(_, newInputValue) => {
                          applyFieldKeyAtIndex(varIndex, newInputValue);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Field Key"
                            helperText={
                              variable.fieldKey && !availableFieldKeys.includes(variable.fieldKey)
                                ? newFieldCreatedOnHostMessage
                                : fieldKeyAutocompleteHint
                            }
                          />
                        )}
                      />
                      {variable.logsTemplateFieldSource ? (
                        <Typography variant="caption" color="text.secondary" sx={{ pl: 0.5 }}>
                          Stored source:{' '}
                          {LOGS_TEMPLATE_FIELD_SOURCE_LABEL[variable.logsTemplateFieldSource] ??
                            variable.logsTemplateFieldSource}
                        </Typography>
                      ) : null}
                    </Stack>
                  </Grid>

                  <Grid size={{ xs: 12, md: 1 }}>
                    {mode === 'Edit' && (
                      <IconButton
                        onClick={() => deleteVariable(varIndex)}
                        color="error"
                        size="small"
                      >
                        <Delete />
                      </IconButton>
                    )}
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}
        </Stack>
      ) : (
        <Typography color="text.secondary" textAlign="center">
          No variables configured. {mode === 'Edit' && 'Click "Add Variable" to create one.'}
        </Typography>
      )}
    </Stack>
  );
};
